/**
 * Lista de grupos activos de pedidos N2
 */
import React from 'react';
import {
    Box,
    Typography,
    Button,
    IconButton,
    Stack,
    Chip
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import Utils from '../../../../utils/Utils';
import { BADGE_COLORS, getBadgeStyles } from '../../../../components/ui/badgeColors';
import BotonEtiquetaGrupoN2 from '../ui/BotonEtiquetaGrupoN2';

/**
 * Color del badge de progreso según lo avanzado que esté el grupo.
 * Sigue la semántica de la paleta: gris sin carga (nada empezado), ámbar en proceso
 * y verde completado. El verde queda libre para esto porque el preparador usa PURPLE.
 */
const getColorProgreso = (progreso) => {
    if (progreso >= 100) return BADGE_COLORS.GREEN;
    if (progreso === 0) return BADGE_COLORS.GRAY;
    return BADGE_COLORS.AMBER;
};

/**
 * Lista de grupos activos con opción de continuar, imprimir su etiqueta de preparación,
 * eliminar o forzar como completado.
 * Se renderiza como sección dentro del Card único de la página (sin Card propio).
 * @param {Object} props
 * @param {Array} props.grupos - Array de grupos activos
 * @param {Function} props.onActualizar - Callback para recargar grupos
 * @param {Function} props.onContinuar - Callback al continuar grupo (recibe grupoId)
 * @param {Function} [props.onEliminar] - Eliminar grupo (recibe el grupo). Sin él no se ofrece
 * @param {Function} [props.onCompletar] - Forzar el grupo como completado (recibe el grupo). Sin él no se ofrece
 * @param {Function} props.getPedidosGrupo - Helper para extraer array de números de pedido
 * @param {Object} [props.productosPorGrupo] - Productos precargados por id de grupo
 *   (useProductosGruposN2): `undefined` mientras cargan, `null` si fallaron
 */
const ListaGruposActivos = ({
    grupos,
    onActualizar,
    onContinuar,
    onEliminar,
    onCompletar,
    getPedidosGrupo,
    productosPorGrupo = {}
}) => {
    if (grupos.length === 0) {
        return null;
    }

    return (
        <Box
            sx={{
                p: 2,
                border: '2px solid #cc071e',
                borderRadius: 1
            }}
        >
            {/* Header */}
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: { xs: 1.5, sm: 2 } }}>
                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
                    Grupos Activos ({grupos.length})
                </Typography>
                <Button
                    variant="outlined"
                    size="small"
                    onClick={onActualizar}
                    sx={{
                        fontSize: { xs: 12, sm: 13 },
                        px: { xs: 1.5, sm: 2 },
                        color: '#cc071e',
                        borderColor: '#cc071e',
                        '&:hover': {
                            borderColor: '#a30618',
                            backgroundColor: 'rgba(204, 7, 30, 0.04)'
                        }
                    }}
                >
                    Actualizar
                </Button>
            </Stack>

            {/* Lista de grupos */}
            <Stack spacing={{ xs: 1.5, sm: 2 }}>
                {grupos.map((grupo) => {
                    const pedidosArray = getPedidosGrupo(grupo);
                    const estadisticas = grupo.estadisticas || {};
                    const progreso = estadisticas.progreso || 0;

                    // Precargados para imprimir sin esperar; null o undefined: carga al pulsar
                    const productos = productosPorGrupo[grupo.id];

                    return (
                        <Box
                            key={grupo.id}
                            sx={{
                                p: { xs: 1.5, sm: 2 },
                                backgroundColor: '#f8f9fa',
                                borderRadius: 1,
                                border: '1px solid #dee2e6',
                                cursor: 'pointer'
                            }}
                            onClick={() => onContinuar(grupo.id)}
                        >
                            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={{ xs: 1, sm: 1.5 }}>
                                {/* Info del grupo */}
                                <Box sx={{ flex: 1 }}>
                                    <Typography sx={{ fontWeight: 600, fontSize: { xs: 14, sm: 16 }, mb: { xs: 0.5, sm: 0.75 } }}>
                                        {grupo.nombre}
                                    </Typography>

                                    {/* Badges de estadísticas */}
                                    <Stack direction="row" spacing={{ xs: 0.75, sm: 1 }} sx={{ mb: { xs: 0.75, sm: 1 } }} flexWrap="wrap" useFlexGap>
                                        <Chip
                                            label={`${estadisticas.totalProductos ?? 0} productos`}
                                            size="small"
                                            sx={getBadgeStyles(BADGE_COLORS.BLUE, { height: { xs: 20, sm: 22 }, fontSize: { xs: 10, sm: 11 } })}
                                        />
                                        <Chip
                                            label={`${estadisticas.totalPedidos ?? pedidosArray.length} pedidos`}
                                            size="small"
                                            sx={getBadgeStyles(BADGE_COLORS.TEAL, { height: { xs: 20, sm: 22 }, fontSize: { xs: 10, sm: 11 } })}
                                        />
                                        <Chip
                                            label={`${progreso}%`}
                                            size="small"
                                            sx={getBadgeStyles(getColorProgreso(progreso), { height: { xs: 20, sm: 22 }, fontSize: { xs: 10, sm: 11 } })}
                                        />
                                        {grupo.preparador && (
                                            /* PURPLE, no GREEN: el verde significa "completado" en la paleta
                                               y aquí solo identificamos a quién lo tiene asignado */
                                            <Chip
                                                label={Utils.toCapitalize(grupo.preparador)}
                                                size="small"
                                                sx={getBadgeStyles(BADGE_COLORS.PURPLE, { height: { xs: 20, sm: 22 }, fontSize: { xs: 10, sm: 11 } })}
                                            />
                                        )}
                                    </Stack>

                                    {/* Lista de pedidos como chips */}
                                    <Box>
                                        <Typography variant="body2" sx={{ fontWeight: 600, mb: { xs: 0.75, sm: 1 }, color: '#6c757d', fontSize: { xs: 11, sm: 12 } }}>
                                            Pedidos ({pedidosArray.length}):
                                        </Typography>
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 0.5, sm: 0.75 } }}>
                                            {pedidosArray.slice(0, 6).map(numero => (
                                                <Chip
                                                    key={numero}
                                                    label={numero}
                                                    size="small"
                                                    sx={getBadgeStyles(BADGE_COLORS.BLUE, { height: { xs: 18, sm: 20 }, fontSize: { xs: 9, sm: 10 } })}
                                                />
                                            ))}
                                            {pedidosArray.length > 6 && (
                                                <Chip
                                                    label={`+${pedidosArray.length - 6}`}
                                                    size="small"
                                                    sx={getBadgeStyles(BADGE_COLORS.GRAY, { height: { xs: 18, sm: 20 }, fontSize: { xs: 9, sm: 10 } })}
                                                />
                                            )}
                                        </Box>
                                    </Box>
                                </Box>

                                {/* Acciones: continuar se hace pinchando la propia fila */}
                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={0.5}>
                                    <BotonEtiquetaGrupoN2 grupo={grupo} productos={productos || undefined} />
                                    {/* Completar a mano es la salida de los grupos colgados que ya
                                        tienen punteo y el backend no deja borrar */}
                                    {onCompletar && (
                                        <IconButton
                                            size="small"
                                            aria-label="Forzar grupo como completado"
                                            title="Forzar como completado"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onCompletar(grupo);
                                            }}
                                            sx={{
                                                color: '#6c757d',
                                                '&:hover': { color: '#196f3d' },
                                                p: { xs: 0.5, sm: 0.75 }
                                            }}
                                        >
                                            <DoneAllIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />
                                        </IconButton>
                                    )}
                                    {/* Por estado y no por progreso: un grupo EN_PROCESO sin punteo
                                        (tras imprimir) tiene 0% y el backend rechaza borrarlo */}
                                    {onEliminar && grupo.estado === 'PENDIENTE' && (
                                        <IconButton
                                            size="small"
                                            aria-label="Eliminar grupo"
                                            title="Eliminar grupo"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onEliminar(grupo);
                                            }}
                                            sx={{
                                                color: '#6c757d',
                                                '&:hover': { color: '#cc071e' },
                                                p: { xs: 0.5, sm: 0.75 }
                                            }}
                                        >
                                            <DeleteIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />
                                        </IconButton>
                                    )}
                                </Stack>
                            </Stack>
                        </Box>
                    );
                })}
            </Stack>
        </Box>
    );
};

export default ListaGruposActivos;
